/**
 * SVG Skill Icons — shared icon registry for system skills.
 *
 * Convention: metadata.json `icon` field uses `svg:<key>` prefix.
 * Example: `"icon": "svg:skills"` renders the layers icon from the sidebar.
 */
import type { ReactNode } from "react";
import { AgentsSparkleIcon } from "@/components/icons/AgentsSparkleIcon";
import { SkillLayersIcon } from "@/components/icons/SkillLayersIcon";
import { RoutinesIcon } from "@/components/icons/RoutinesNavIcon";

/** Check if an icon string uses the `svg:` prefix convention. */
export function isSvgIcon(icon: string | undefined | null): boolean {
  return !!icon && icon.startsWith("svg:");
}

/** Extract the key from a `svg:<key>` icon string. */
function getSvgKey(icon: string): string {
  return icon.slice(4);
}

const SVG_ICON_COMPONENTS: Record<
  string,
  (props: { width: number; height: number }) => ReactNode
> = {
  agents: (props) => <AgentsSparkleIcon {...props} />,
  skills: (props) => <SkillLayersIcon {...props} />,
  routines: (props) => <RoutinesIcon {...props} />,
};

/** Render an SVG skill icon at the given size. Returns null if key is unknown. */
export function SvgSkillIcon({
  icon,
  size,
}: {
  icon: string;
  size: number;
}): ReactNode {
  const Component = SVG_ICON_COMPONENTS[getSvgKey(icon)];
  if (!Component) return null;
  return <Component width={size} height={size} />;
}

/** Get an SVG icon React node at 16px (for inline/dropdown use). Returns null if not an svg: icon. */
export function getSvgSkillIcon(iconName: string | undefined): ReactNode {
  if (!isSvgIcon(iconName)) return null;
  return <SvgSkillIcon icon={iconName!} size={16} />;
}
